import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Banknote,
  Building2,
  CalendarDays,
  Coins,
  FileText,
  Layers,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { AllowanceAssignmentsPanel } from "../components/AllowanceAssignmentsPanel";
import { Badge } from "../components/Badge";
import { ErrorPanel, Loading } from "../components/Loading";
import { OverrideStackViewer } from "../components/OverrideStackViewer";
import { DatePicker, EnumSelect } from "../components/ui";
import type { EnumKind } from "../lib/enums";
import { api, ApiError } from "../lib/api";
import type { ContractDetail } from "../lib/api";

// 5-tab Contract detail page (M12.7).
//
// Tabs: General · Payroll Setup · Leave & EOS · Allowances · Override Stack
// (the spec's "Position Allowances" tab is renamed to "Override Stack" since
// that's the panel HR uses most — Position-allowance is a one-layer subset
// of what's shown).

const TABS = [
  { key: "general", labelKey: "contract_detail.tab_general", icon: FileText },
  { key: "payroll", labelKey: "contract_detail.tab_payroll", icon: Banknote },
  { key: "leave_eos", labelKey: "contract_detail.tab_leave_eos", icon: CalendarDays },
  { key: "allowances", labelKey: "contract_detail.tab_allowances", icon: Coins },
  { key: "stack", labelKey: "contract_detail.tab_stack", icon: Layers },
] as const;
type TabKey = (typeof TABS)[number]["key"];

function statusTone(s: string | null): "green" | "amber" | "slate" | "rose" {
  switch (s) {
    case "active":
      return "green";
    case "draft":
      return "slate";
    case "suspended":
      return "amber";
    case "terminated":
      return "rose";
    default:
      return "slate";
  }
}

// Inline-editable field (string-valued). Calls PATCH on blur/Enter,
// invalidates the contract query on success.
function EditableContractField({
  contractId,
  label,
  fieldName,
  value,
  parser = (v: string) => v,
  postfix,
  type = "text",
  min,
  max,
  selectOptions,
  enumKind,
}: {
  contractId: string;
  label: string;
  fieldName: string;
  value: string | number | null;
  parser?: (v: string) => string | number | boolean;
  postfix?: string;
  /** Native input type — drives the picker / keyboard. Defaults to text. */
  type?: "text" | "date" | "email" | "tel" | "number";
  /** Optional bounds for date/number. */
  min?: string;
  max?: string;
  /**
   * When supplied, the inline editor renders a <select> instead of an
   * <input>. Each option is `[value, label]`. Drops free-text in
   * favor of a controlled bounded list — the right default for any
   * column with a CHECK constraint or enum.
   */
  selectOptions?: ReadonlyArray<readonly [string, string]>;
  /**
   * M3X.UI-HYGIENE-PASS-2 PR 1 — when supplied, the inline editor
   * renders `<EnumSelect kind={enumKind}>` with the bilingual option
   * set sourced from `lib/enums/catalog.ts`. Takes precedence over
   * the legacy `selectOptions` array.
   */
  enumKind?: EnumKind;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value === null ? "" : String(value));
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: (v: string) =>
      api.updateContract(contractId, {
        [fieldName]: v === "" ? null : parser(v),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contract", contractId] });
      setEditing(false);
      setErrorMsg(null);
    },
    onError: (e: unknown) => {
      setErrorMsg(e instanceof ApiError ? e.detail : String(e));
    },
  });

  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
        {label}
      </span>
      {editing ? (
        <div className="flex items-center gap-2">
          {enumKind ? (
            <EnumSelect
              kind={enumKind}
              value={draft || null}
              onChange={(next) => {
                const v = next ?? "";
                setDraft(v);
                mutation.mutate(v);
              }}
              showDescriptions
            />
          ) : type === "date" ? (
            <DatePicker
              value={draft || null}
              onChange={(next) => {
                const v = next ?? "";
                setDraft(v);
                mutation.mutate(v);
              }}
              min={min}
              max={max}
            />
          ) : selectOptions ? (
            <select
              autoFocus
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                mutation.mutate(e.target.value);
              }}
              onBlur={() => setEditing(false)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setEditing(false);
                  setDraft(value === null ? "" : String(value));
                }
              }}
              className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
            >
              {selectOptions.map(([v, label]) => (
                <option key={v} value={v}>
                  {label}
                </option>
              ))}
            </select>
          ) : (
            <input
              autoFocus
              type={type}
              min={min}
              max={max}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={() => mutation.mutate(draft)}
              onKeyDown={(e) => {
                if (e.key === "Enter") mutation.mutate(draft);
                if (e.key === "Escape") {
                  setEditing(false);
                  setDraft(value === null ? "" : String(value));
                }
              }}
              className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
            />
          )}
          {postfix && <span className="text-xs text-slate-500">{postfix}</span>}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="text-start text-sm text-slate-900 hover:text-slate-700"
        >
          {value === null || value === "" ? (
            <span className="italic text-slate-400">—</span>
          ) : (
            <span className="font-mono tabular-nums">
              {String(value)}
              {postfix && (
                <span className="ms-1 text-xs text-slate-500">{postfix}</span>
              )}
            </span>
          )}
        </button>
      )}
      {errorMsg && <p className="text-xs text-rose-600">{errorMsg}</p>}
    </div>
  );
}

// Boolean toggle field — clicking flips the value.
function BooleanField({
  contractId,
  label,
  fieldName,
  value,
}: {
  contractId: string;
  label: string;
  fieldName: string;
  value: boolean;
}) {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: (next: boolean) =>
      api.updateContract(contractId, { [fieldName]: next }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contract", contractId] });
    },
  });
  return (
    <label className="flex items-center justify-between gap-2 rounded border border-slate-200 px-3 py-2 text-sm">
      <span className="text-slate-700">{label}</span>
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => mutation.mutate(e.target.checked)}
        className="h-4 w-4"
      />
    </label>
  );
}

function GeneralTab({ c }: { c: ContractDetail }) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-2 gap-4">
      <EditableContractField
        contractId={c.id}
        label={t("contract_detail.contract_status")}
        fieldName="contract_status"
        value={c.contract_status}
        enumKind="contract_status"
      />
      <EditableContractField
        contractId={c.id}
        label={t("contract_detail.contract_period")}
        fieldName="contract_period"
        value={c.contract_period}
        enumKind="contract_period"
      />
      <EditableContractField
        contractId={c.id}
        label={t("contract_detail.employment_type")}
        fieldName="employment_type"
        value={c.employment_type}
        enumKind="employment_type"
      />
      <EditableContractField
        contractId={c.id}
        label={t("contract_detail.end_date")}
        fieldName="end_date"
        value={c.end_date}
        type="date"
      />
      <div className="col-span-2">
        <EditableContractField
          contractId={c.id}
          label={t("contract_detail.note")}
          fieldName="note"
          value={c.note}
        />
      </div>
    </div>
  );
}

function PayrollTab({ c }: { c: ContractDetail }) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-2 gap-4">
      <EditableContractField
        contractId={c.id}
        label={t("contract_detail.pay_frequency")}
        fieldName="pay_frequency"
        value={c.pay_frequency}
        selectOptions={[
          ["monthly", t("contract_detail.pay_monthly", "Monthly")],
          ["weekly", t("contract_detail.pay_weekly", "Weekly")],
          ["daily", t("contract_detail.pay_daily", "Daily")],
          ["piecework", t("contract_detail.pay_piecework", "Piecework")],
        ]}
      />
      <EditableContractField
        contractId={c.id}
        label={t("contract_detail.basic_salary")}
        fieldName="basic_salary_amount"
        value={c.basic_salary_amount}
        postfix={c.basic_salary_currency}
      />
      <EditableContractField
        contractId={c.id}
        label={t("contract_detail.work_permit_profession")}
        fieldName="work_permit_profession"
        value={c.work_permit_profession}
      />
      <BooleanField
        contractId={c.id}
        label={t("contract_detail.exclude_from_wps")}
        fieldName="exclude_from_wps"
        value={c.exclude_from_wps}
      />
    </div>
  );
}

function LeaveEosTab({ c }: { c: ContractDetail }) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-2 gap-4">
      <EditableContractField
        contractId={c.id}
        label={t("contract_detail.probation_period_days")}
        fieldName="probation_period_days"
        value={c.probation_period_days}
        parser={(v) => Number(v)}
      />
      <div className="flex flex-col gap-1">
        <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
          {t("contract_detail.probation_ends")}
        </span>
        <span className="text-sm font-mono tabular-nums">
          {c.probation_end_date ?? "—"}
        </span>
      </div>
      <BooleanField
        contractId={c.id}
        label={t("contract_detail.stop_leave_accrual")}
        fieldName="stop_leave_accrual"
        value={c.stop_leave_accrual}
      />
      <BooleanField
        contractId={c.id}
        label={t("contract_detail.stop_ticket_accrual")}
        fieldName="stop_ticket_accrual"
        value={c.stop_ticket_accrual}
      />
      <BooleanField
        contractId={c.id}
        label={t("contract_detail.stop_si_accrual")}
        fieldName="stop_si_accrual"
        value={c.stop_si_accrual}
      />
      <BooleanField
        contractId={c.id}
        label={t("contract_detail.stop_eos_accrual")}
        fieldName="stop_eos_accrual"
        value={c.stop_eos_accrual}
      />
      <BooleanField
        contractId={c.id}
        label={t("contract_detail.stop_medical_insurance")}
        fieldName="stop_medical_insurance_accrual"
        value={c.stop_medical_insurance_accrual}
      />
    </div>
  );
}

function AllowancesTab({ c }: { c: ContractDetail }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <EditableContractField
          contractId={c.id}
          label={t("contract_detail.housing_allowance")}
          fieldName="housing_allowance_amount"
          value={c.housing_allowance_amount}
          postfix={c.housing_allowance_currency}
        />
        <EditableContractField
          contractId={c.id}
          label={t("contract_detail.transport_allowance")}
          fieldName="transportation_allowance_amount"
          value={c.transportation_allowance_amount}
          postfix={c.transportation_allowance_currency}
        />
        <EditableContractField
          contractId={c.id}
          label={t("contract_detail.food_allowance")}
          fieldName="food_allowance_amount"
          value={c.food_allowance_amount}
          postfix={c.food_allowance_currency}
        />
        <EditableContractField
          contractId={c.id}
          label={t("contract_detail.other_fixed_allowances")}
          fieldName="other_fixed_allowances_amount"
          value={c.other_fixed_allowances_amount}
          postfix={c.other_fixed_allowances_currency}
        />
        <BooleanField
          contractId={c.id}
          label={t("contract_detail.housing_by_company")}
          fieldName="housing_by_company"
          value={c.housing_by_company}
        />
        <BooleanField
          contractId={c.id}
          label={t("contract_detail.transportation_by_company")}
          fieldName="transportation_by_company"
          value={c.transportation_by_company}
        />
      </div>

      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">
          {t("contract_detail.comp_overrides_raw")}
        </p>
        <pre className="mt-2 overflow-x-auto text-xs text-slate-700">
          {JSON.stringify(c.comp_overrides, null, 2)}
        </pre>
      </div>

      {c.legal_entity_id && (
        <AllowanceAssignmentsPanel
          layer="contract"
          parentId={c.id}
          legalEntityId={c.legal_entity_id}
        />
      )}
    </div>
  );
}

function StackTab({ c }: { c: ContractDetail }) {
  const { t } = useTranslation();
  const components = Object.keys(c.resolution_paths);
  if (components.length === 0) {
    return (
      <p className="text-sm italic text-slate-500">
        {t("contract_detail.no_resolution_paths")}
      </p>
    );
  }
  return (
    <div className="space-y-4">
      {components.map((name) => (
        <OverrideStackViewer
          key={name}
          componentName={name}
          path={c.resolution_paths[name]}
        />
      ))}
    </div>
  );
}

function Header({ c }: { c: ContractDetail }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">
            {t("contract_detail.header_prefix")}{" "}
            {c.contract_number !== null
              ? `#${c.contract_number.toString().padStart(6, "0")}`
              : t("contract_detail.unnumbered")}
          </p>
          <h1 className="mt-1 text-2xl font-bold text-slate-900">
            {c.employee.first_name_en} {c.employee.last_name_en}
            <span className="ms-2 text-sm font-normal text-slate-500">
              ({c.employee.employee_code})
            </span>
          </h1>
          {c.position && (
            <p className="mt-2 flex items-center gap-1.5 text-sm text-slate-700">
              <Building2 className="h-4 w-4 text-slate-400" />
              {c.position.title_en} ·{" "}
              <span className="font-mono">{c.position.position_number_display}</span>
            </p>
          )}
          <p className="mt-1 text-sm text-slate-500">
            {c.start_date} → {c.end_date ?? t("contract_detail.unlimited")}
          </p>
        </div>
        <Badge tone={statusTone(c.contract_status)}>
          {c.contract_status ?? "—"}
        </Badge>
      </div>
    </div>
  );
}

export function ContractDetailPage() {
  const { t } = useTranslation();
  const { contractId } = useParams<{ contractId: string }>();
  const [active, setActive] = useState<TabKey>("general");

  const { data, isLoading, error } = useQuery({
    queryKey: ["contract", contractId],
    queryFn: () => api.getContract(contractId!),
    enabled: !!contractId,
  });

  if (isLoading) return <Loading />;
  if (error) return <ErrorPanel message={(error as Error).message} />;
  if (!data) return null;

  return (
    <div className="flex flex-col gap-4">
      <Header c={data} />

      <div className="flex items-center gap-1 overflow-x-auto rounded-xl border border-slate-200 bg-white p-1">
        {TABS.map(({ key, labelKey, icon: Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => setActive(key)}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition ${
              active === key
                ? "bg-brand-blue text-white"
                : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <Icon className="h-4 w-4" />
            {t(labelKey)}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        {active === "general" && <GeneralTab c={data} />}
        {active === "payroll" && <PayrollTab c={data} />}
        {active === "leave_eos" && <LeaveEosTab c={data} />}
        {active === "allowances" && <AllowancesTab c={data} />}
        {active === "stack" && <StackTab c={data} />}
      </div>
    </div>
  );
}
